import { useToast } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AiInterviewQuestion } from "../../../graphql";
import { useAiInterviewerContext } from "../AiInterviewerContext";
import { useAiInterviewerFormContext } from "../useAiInterviewerFormContext";
import {
  applySuggestionFieldChangesToQuestion,
  GuideQuestionSuggestionsBucketV2,
  GuideQuestionSuggestionV2,
  prepareSuggestionFieldChangesForPreview,
  QuestionSuggestionPreviewState,
  SuggestionConflictReason,
  SuggestionFieldChange,
  suggestionFieldKey,
  SuggestionFieldTarget,
  suggestionTargetsConflict,
  suggestionTargetsFromChanges,
} from "./types";

type UseGuideAssistantPreviewStateArgs = {
  questions: AiInterviewQuestion[];
  questionSuggestions: GuideQuestionSuggestionsBucketV2[];
  pendingAcceptedSuggestionIds: ReadonlySet<string>;
  refreshSuggestions: () => Promise<void>;
  ignoreSuggestion: (suggestionId: string) => Promise<boolean>;
  onAcceptSuggestion: (suggestionId: string) => void;
};

type UseGuideAssistantPreviewStateResult = {
  visibleQuestionSuggestions: GuideQuestionSuggestionsBucketV2[];
  visibleSuggestionCount: number;
  activeQuestionId: string | null;
  highlightedQuestionId: string | null;
  selectedSuggestionId: string | null;
  ignoringSuggestionId: string | null;
  applyingSuggestionId: string | null;
  previewsByQuestionId: Record<
    string,
    (QuestionSuggestionPreviewState & { questionIndex: number }) | null
  >;
  suggestionConflictReasons: Record<string, SuggestionConflictReason>;
  handleResolveField: (
    questionId: string,
    fallbackQuestionIndex: number,
    target: SuggestionFieldTarget,
    decision: "accept" | "reject"
  ) => void;
  handleResolveAllFields: (
    questionId: string,
    fallbackQuestionIndex: number
  ) => void;
  handleSelectSuggestion: (suggestion: GuideQuestionSuggestionV2) => void;
  handleSelectQuestionTab: (
    questionId: string,
    suggestionQuestionIndex: number
  ) => void;
  handleIgnoreSuggestion: (suggestionId: string) => Promise<void>;
  handleIgnorePreview: (
    questionId: string,
    fallbackQuestionIndex: number
  ) => Promise<void>;
  handleRefreshSuggestions: () => void;
  handleHoverQuestion: (
    questionId: string | null,
    questionIndex: number | null
  ) => void;
};

const getUnresolvedPreviewTargets = (
  preview: QuestionSuggestionPreviewState
): SuggestionFieldTarget[] =>
  preview.affectedFields.filter(
    (target) => !preview.resolvedFields[suggestionFieldKey(target)]
  );

const useGuideAssistantPreviewState = ({
  questions,
  questionSuggestions,
  pendingAcceptedSuggestionIds,
  refreshSuggestions,
  ignoreSuggestion,
  onAcceptSuggestion,
}: UseGuideAssistantPreviewStateArgs): UseGuideAssistantPreviewStateResult => {
  const aiInterviewer = useAiInterviewerContext();
  const { getValues, setValue } = useAiInterviewerFormContext();
  const toast = useToast();
  const [activeQuestionId, setActiveQuestionId] = useState<string | null>(null);
  const [highlightedQuestionId, setHighlightedQuestionId] = useState<
    string | null
  >(null);
  const [selectedSuggestionId, setSelectedSuggestionId] = useState<
    string | null
  >(null);
  const [ignoringSuggestionId, setIgnoringSuggestionId] = useState<
    string | null
  >(null);
  const [applyingSuggestionId, setApplyingSuggestionId] = useState<
    string | null
  >(null);
  const [dismissedSuggestionIds, setDismissedSuggestionIds] = useState<
    Set<string>
  >(new Set());
  const [acceptedFieldLocksByQuestionId, setAcceptedFieldLocksByQuestionId] =
    useState<Record<string, SuggestionFieldTarget[]>>({});
  const [previewsByQuestionId, setPreviewsByQuestionId] = useState<
    Record<
      string,
      (QuestionSuggestionPreviewState & { questionIndex: number }) | null
    >
  >({});
  const previousSavedQuestionIdentitySignatureRef = useRef<string | null>(null);

  const savedQuestionIdentitySignature = useMemo(
    () =>
      [...(aiInterviewer.currentVersion?.questions ?? [])]
        .sort(
          (questionA, questionB) => questionA.sortOrder - questionB.sortOrder
        )
        .map((question) => question.id)
        .join("|"),
    [aiInterviewer.currentVersion?.questions]
  );

  const visibleQuestionSuggestions = useMemo(() => {
    const questionIdByOriginalQuestionId = new Map(
      questions.map((question) => [
        question.originalQuestionId ?? question.id,
        question.id,
      ])
    );
    const questionIndexByOriginalQuestionId = new Map(
      questions.map((question, index) => [
        question.originalQuestionId ?? question.id,
        index,
      ])
    );
    return questionSuggestions
      .filter((bucket) =>
        questionIdByOriginalQuestionId.has(bucket.originalQuestionId)
      )
      .map((bucket) => ({
        ...bucket,
        questionIndex:
          questionIndexByOriginalQuestionId.get(bucket.originalQuestionId) ??
          bucket.questionIndex,
        questionId:
          questionIdByOriginalQuestionId.get(bucket.originalQuestionId) ??
          bucket.questionId,
        suggestions: bucket.suggestions
          .filter(
            (suggestion) =>
              !dismissedSuggestionIds.has(suggestion.id) &&
              !pendingAcceptedSuggestionIds.has(suggestion.id)
          )
          .map((suggestion) => ({
            ...suggestion,
            questionId:
              questionIdByOriginalQuestionId.get(bucket.originalQuestionId) ??
              suggestion.questionId,
          })),
      }))
      .filter(
        (bucket) =>
          bucket.suggestions.length > 0 || Boolean(bucket.analysisError)
      )
      .sort((a, b) => a.questionIndex - b.questionIndex);
  }, [
    dismissedSuggestionIds,
    pendingAcceptedSuggestionIds,
    questionSuggestions,
    questions,
  ]);

  const visibleSuggestionCount = useMemo(
    () =>
      visibleQuestionSuggestions.reduce(
        (acc, bucket) => acc + bucket.suggestions.length,
        0
      ),
    [visibleQuestionSuggestions]
  );

  useEffect(() => {
    const previousSignature = previousSavedQuestionIdentitySignatureRef.current;
    previousSavedQuestionIdentitySignatureRef.current =
      savedQuestionIdentitySignature;

    if (
      previousSignature == null ||
      previousSignature === savedQuestionIdentitySignature
    ) {
      return;
    }

    // Saved question identities changed underneath the form, so clear
    // assistant-local previews and selection state built from the older guide.
    setPreviewsByQuestionId({});
    setSelectedSuggestionId(null);
    setAcceptedFieldLocksByQuestionId({});
    setActiveQuestionId(null);
    setHighlightedQuestionId(null);
    setIgnoringSuggestionId(null);
    setApplyingSuggestionId(null);
  }, [savedQuestionIdentitySignature]);

  useEffect(() => {
    if (visibleQuestionSuggestions.length === 0) {
      if (activeQuestionId) {
        setActiveQuestionId(null);
      }
      return;
    }

    const firstQuestionWithSuggestions = visibleQuestionSuggestions.find(
      (bucket) => bucket.suggestions.length > 0
    );
    const defaultQuestionId =
      firstQuestionWithSuggestions?.questionId ??
      visibleQuestionSuggestions[0]?.questionId ??
      null;

    if (!activeQuestionId) {
      if (defaultQuestionId) {
        setActiveQuestionId(defaultQuestionId);
      }
      return;
    }

    const activeQuestionStillExists = visibleQuestionSuggestions.some(
      (bucket) => bucket.questionId === activeQuestionId
    );
    if (!activeQuestionStillExists && defaultQuestionId) {
      setActiveQuestionId(defaultQuestionId);
    }
  }, [activeQuestionId, visibleQuestionSuggestions]);

  const resolveQuestionReference = useCallback(
    (
      questionId: string,
      fallbackIndex: number
    ): {
      questionIndex: number;
      fieldQuestionId: string;
      question: AiInterviewQuestion | undefined;
      matchedById: boolean;
    } => {
      const formQuestions = getValues().questions;
      const byIdIndex = formQuestions.findIndex(
        (question) => question.id === questionId
      );
      const matchedById = byIdIndex >= 0;
      const questionIndex = matchedById ? byIdIndex : fallbackIndex;
      const question = matchedById ? formQuestions[byIdIndex] : undefined;
      const fieldQuestionId =
        questions[questionIndex]?.id ?? question?.id ?? questionId;
      return {
        questionIndex,
        fieldQuestionId: fieldQuestionId ?? questionId,
        question,
        matchedById,
      };
    },
    [getValues, questions]
  );

  const resolvePreviewForQuestion = useCallback(
    (
      questionId: string,
      fallbackQuestionIndex: number
    ): (QuestionSuggestionPreviewState & { questionIndex: number }) | null =>
      previewsByQuestionId[questionId] ??
      previewsByQuestionId[`index:${fallbackQuestionIndex}`] ??
      null,
    [previewsByQuestionId]
  );

  useEffect(() => {
    if (!activeQuestionId) {
      return;
    }
    const activeBucket = visibleQuestionSuggestions.find(
      (bucket) => bucket.questionId === activeQuestionId
    );
    const fallbackIndex = activeBucket?.questionIndex ?? 0;
    const { fieldQuestionId } = resolveQuestionReference(
      activeQuestionId,
      fallbackIndex
    );
    setHighlightedQuestionId(fieldQuestionId);
  }, [activeQuestionId, resolveQuestionReference, visibleQuestionSuggestions]);

  const getFieldChangeForTarget = useCallback(
    (
      preview: QuestionSuggestionPreviewState,
      target: SuggestionFieldTarget
    ): SuggestionFieldChange | null => {
      const targetKey = suggestionFieldKey(target);
      return (
        preview.fieldChanges.find(
          (change) => suggestionFieldKey(change) === targetKey
        ) ?? null
      );
    },
    []
  );

  const applyAcceptedField = useCallback(
    (
      questionIndex: number,
      preview: QuestionSuggestionPreviewState,
      target: SuggestionFieldTarget
    ): void => {
      const fieldChange = getFieldChangeForTarget(preview, target);
      switch (target.field) {
        case "QUESTION":
          setValue(
            `questions.${questionIndex}.question`,
            fieldChange?.value ?? preview.updatedQuestion.question,
            {
              shouldDirty: true,
            }
          );
          break;
        case "FOLLOWUP": {
          const existing = (
            getValues().questions[questionIndex]?.followups ?? []
          ).map((followup) => (typeof followup === "string" ? followup : ""));

          if (target.positionMode === "APPEND") {
            existing.push(fieldChange?.value ?? "");
            setValue(`questions.${questionIndex}.followups`, existing, {
              shouldDirty: true,
            });
            break;
          }

          if (target.index == null) {
            setValue(
              `questions.${questionIndex}.followups`,
              (preview.updatedQuestion.followups ?? []).map(
                (followup: string | null) =>
                  typeof followup === "string" ? followup : ""
              ),
              { shouldDirty: true }
            );
            break;
          }

          while (existing.length <= target.index) {
            existing.push("");
          }
          const previewFollowupValue =
            preview.updatedQuestion.followups[target.index];
          existing[target.index] =
            fieldChange?.value ??
            (typeof previewFollowupValue === "string"
              ? previewFollowupValue
              : "");
          setValue(`questions.${questionIndex}.followups`, existing, {
            shouldDirty: true,
          });
          break;
        }
        case "RUBRIC": {
          const existing = [
            ...(getValues().questions[questionIndex]?.rubric ?? []),
          ].map((entry) => (typeof entry === "string" ? entry : ""));
          if (target.index == null) {
            setValue(
              `questions.${questionIndex}.rubric`,
              (preview.updatedQuestion.rubric ?? []).map(
                (entry: string | null) =>
                  typeof entry === "string" ? entry : ""
              ),
              { shouldDirty: true }
            );
            break;
          }
          while (existing.length <= target.index) {
            existing.push("");
          }
          const previewRubricValue =
            preview.updatedQuestion.rubric[target.index];
          existing[target.index] =
            fieldChange?.value ??
            (typeof previewRubricValue === "string" ? previewRubricValue : "");
          setValue(`questions.${questionIndex}.rubric`, existing, {
            shouldDirty: true,
          });
          break;
        }
        case "QUALIFICATION_CRITERIA":
          setValue(
            `questions.${questionIndex}.qualificationCriteria`,
            fieldChange?.value ??
              preview.updatedQuestion.qualificationCriteria ??
              "",
            { shouldDirty: true }
          );
          break;
        case "SUMMARY_CRITERIA":
          setValue(
            `questions.${questionIndex}.summaryCriteria`,
            fieldChange?.value ?? preview.updatedQuestion.summaryCriteria ?? "",
            { shouldDirty: true }
          );
          break;
        default:
          break;
      }
    },
    [getFieldChangeForTarget, getValues, setValue]
  );

  const scrollToQuestionCardTop = useCallback(
    (questionId: string, fallbackQuestionIndex?: number): void => {
      // Defer scroll until after React has committed DOM changes. State
      // updates (adding/removing previews) resize cards, shifting layout.
      // Scrolling before that paint lands at a stale position.
      requestAnimationFrame(() => {
        const questionElement = document.getElementById(
          `guide-question-card-${questionId}`
        );
        if (questionElement) {
          questionElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          return;
        }

        if (typeof fallbackQuestionIndex === "number") {
          const fallbackElement = document.querySelector(
            `[data-guide-question-index="${fallbackQuestionIndex}"]`
          );
          if (fallbackElement instanceof HTMLElement) {
            fallbackElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }
      });
    },
    []
  );

  const addAcceptedFieldLock = useCallback(
    (questionId: string, target: SuggestionFieldTarget): void => {
      setAcceptedFieldLocksByQuestionId((prev) => {
        const existingTargets = prev[questionId] ?? [];
        const hasTarget = existingTargets.some(
          (existing) =>
            suggestionFieldKey(existing) === suggestionFieldKey(target)
        );
        if (hasTarget) {
          return prev;
        }
        return {
          ...prev,
          [questionId]: [
            ...existingTargets,
            {
              field: target.field,
              index: target.index ?? null,
              positionMode: target.positionMode ?? null,
              changeIndex: target.changeIndex ?? null,
            },
          ],
        };
      });
    },
    []
  );

  const handleIgnoreSuggestion = useCallback(
    async (suggestionId: string): Promise<void> => {
      setIgnoringSuggestionId(suggestionId);
      try {
        const success = await ignoreSuggestion(suggestionId);
        if (!success) {
          toast({
            status: "error",
            title: "Failed to ignore suggestion. Please try again.",
          });
          return;
        }

        setDismissedSuggestionIds((prev) => new Set(prev).add(suggestionId));
        setSelectedSuggestionId((prev) =>
          prev === suggestionId ? null : prev
        );
        setPreviewsByQuestionId((prev) => {
          const next = { ...prev };
          Object.entries(next).forEach(([questionId, preview]) => {
            if (preview?.suggestionId === suggestionId) {
              next[questionId] = null;
            }
          });
          return next;
        });
      } catch {
        toast({
          status: "error",
          title: "Failed to ignore suggestion. Please try again.",
        });
      } finally {
        setIgnoringSuggestionId(null);
      }
    },
    [ignoreSuggestion, toast]
  );

  const completePreviewIfDone = useCallback(
    (
      questionId: string,
      questionIndex: number,
      nextPreview: QuestionSuggestionPreviewState & { questionIndex: number }
    ): void => {
      const totalTargets = nextPreview.affectedFields.length;
      const resolvedTargets = Object.keys(nextPreview.resolvedFields).length;
      if (resolvedTargets < totalTargets) {
        return;
      }

      const clearSelectedPreview = (): void => {
        setSelectedSuggestionId((prev) =>
          prev === nextPreview.suggestionId ? null : prev
        );
        setPreviewsByQuestionId((prev) => ({
          ...prev,
          [questionId]: null,
          [`index:${questionIndex}`]: null,
        }));
      };

      if (nextPreview.acceptedCount > 0) {
        onAcceptSuggestion(nextPreview.suggestionId);
        setDismissedSuggestionIds((prev) =>
          new Set(prev).add(nextPreview.suggestionId)
        );
        clearSelectedPreview();
        return;
      }

      clearSelectedPreview();
    },
    [onAcceptSuggestion]
  );

  const handleResolveField = useCallback(
    (
      questionId: string,
      fallbackQuestionIndex: number,
      target: SuggestionFieldTarget,
      decision: "accept" | "reject"
    ): void => {
      const currentPreview = resolvePreviewForQuestion(
        questionId,
        fallbackQuestionIndex
      );
      if (!currentPreview) {
        return;
      }
      if (applyingSuggestionId === currentPreview.suggestionId) {
        return;
      }

      const key = suggestionFieldKey(target);
      if (currentPreview.resolvedFields[key]) {
        return;
      }

      const { questionIndex, fieldQuestionId, matchedById } =
        resolveQuestionReference(questionId, fallbackQuestionIndex);
      if (!matchedById) {
        return;
      }
      if (decision === "accept") {
        applyAcceptedField(questionIndex, currentPreview, target);
        addAcceptedFieldLock(fieldQuestionId, target);
      }
      const resolvedDecision: "accepted" | "rejected" =
        decision === "accept" ? "accepted" : "rejected";

      const nextPreview = {
        ...currentPreview,
        resolvedFields: {
          ...currentPreview.resolvedFields,
          [key]: resolvedDecision,
        },
        acceptedCount:
          currentPreview.acceptedCount + (decision === "accept" ? 1 : 0),
      };

      setPreviewsByQuestionId((prev) => ({
        ...prev,
        [fieldQuestionId]: nextPreview,
        [`index:${questionIndex}`]: nextPreview,
      }));

      const resolvedTargets = Object.keys(nextPreview.resolvedFields).length;
      if (
        nextPreview.acceptedCount === 0 &&
        resolvedTargets === nextPreview.affectedFields.length
      ) {
        handleIgnoreSuggestion(nextPreview.suggestionId).catch(() => undefined);
        return;
      }

      completePreviewIfDone(fieldQuestionId, questionIndex, nextPreview);
    },
    [
      addAcceptedFieldLock,
      applyAcceptedField,
      applyingSuggestionId,
      completePreviewIfDone,
      handleIgnoreSuggestion,
      resolvePreviewForQuestion,
      resolveQuestionReference,
    ]
  );

  const handleResolveAllFields = useCallback(
    (questionId: string, fallbackQuestionIndex: number): void => {
      const currentPreview = resolvePreviewForQuestion(
        questionId,
        fallbackQuestionIndex
      );
      if (!currentPreview) {
        return;
      }
      if (applyingSuggestionId === currentPreview.suggestionId) {
        return;
      }

      const unresolvedTargets = getUnresolvedPreviewTargets(currentPreview);
      if (unresolvedTargets.length === 0) {
        return;
      }

      const { questionIndex, fieldQuestionId, matchedById } =
        resolveQuestionReference(questionId, fallbackQuestionIndex);
      if (!matchedById) {
        return;
      }

      unresolvedTargets.forEach((target) => {
        applyAcceptedField(questionIndex, currentPreview, target);
        addAcceptedFieldLock(fieldQuestionId, target);
      });

      const nextResolvedFields = { ...currentPreview.resolvedFields };
      unresolvedTargets.forEach((target) => {
        nextResolvedFields[suggestionFieldKey(target)] = "accepted";
      });

      const nextPreview = {
        ...currentPreview,
        resolvedFields: nextResolvedFields,
        acceptedCount: currentPreview.acceptedCount + unresolvedTargets.length,
      };

      setPreviewsByQuestionId((prev) => ({
        ...prev,
        [fieldQuestionId]: nextPreview,
        [`index:${questionIndex}`]: nextPreview,
      }));
      completePreviewIfDone(fieldQuestionId, questionIndex, nextPreview);
    },
    [
      addAcceptedFieldLock,
      applyAcceptedField,
      applyingSuggestionId,
      completePreviewIfDone,
      resolvePreviewForQuestion,
      resolveQuestionReference,
    ]
  );

  const handleSelectSuggestion = useCallback(
    (suggestion: GuideQuestionSuggestionV2): void => {
      const {
        questionIndex,
        fieldQuestionId,
        question: currentQuestion,
        matchedById,
      } = resolveQuestionReference(
        suggestion.questionId,
        suggestion.questionIndex
      );
      if (!currentQuestion || !matchedById) {
        return;
      }

      const previewFieldChanges = prepareSuggestionFieldChangesForPreview(
        suggestion.fieldChanges
      );
      const affectedFields = suggestionTargetsFromChanges(previewFieldChanges);
      if (affectedFields.length === 0) {
        setDismissedSuggestionIds((prev) => new Set(prev).add(suggestion.id));
        if (selectedSuggestionId === suggestion.id) {
          setSelectedSuggestionId(null);
        }
        return;
      }

      const updatedQuestion = applySuggestionFieldChangesToQuestion(
        currentQuestion,
        previewFieldChanges
      );

      const previewState: QuestionSuggestionPreviewState & {
        questionIndex: number;
      } = {
        suggestionId: suggestion.id,
        updatedQuestion,
        fieldChanges: previewFieldChanges,
        affectedFields,
        resolvedFields: {},
        acceptedCount: 0,
        questionIndex,
      };

      setActiveQuestionId(suggestion.questionId);
      setHighlightedQuestionId(fieldQuestionId);
      setSelectedSuggestionId(suggestion.id);
      setPreviewsByQuestionId({
        [fieldQuestionId]: previewState,
        [`index:${questionIndex}`]: previewState,
      });
      scrollToQuestionCardTop(fieldQuestionId, questionIndex);
    },
    [resolveQuestionReference, scrollToQuestionCardTop, selectedSuggestionId]
  );

  const handleSelectQuestionTab = useCallback(
    (questionId: string, suggestionQuestionIndex: number): void => {
      const { questionIndex, fieldQuestionId } = resolveQuestionReference(
        questionId,
        suggestionQuestionIndex
      );
      setActiveQuestionId(questionId);
      setHighlightedQuestionId(fieldQuestionId);
      scrollToQuestionCardTop(fieldQuestionId, questionIndex);
    },
    [resolveQuestionReference, scrollToQuestionCardTop]
  );

  const handleIgnorePreview = useCallback(
    async (
      questionId: string,
      fallbackQuestionIndex: number
    ): Promise<void> => {
      const currentPreview = resolvePreviewForQuestion(
        questionId,
        fallbackQuestionIndex
      );
      if (!currentPreview) {
        return;
      }

      if (currentPreview.acceptedCount === 0) {
        await handleIgnoreSuggestion(currentPreview.suggestionId);
        return;
      }

      const unresolvedTargets = getUnresolvedPreviewTargets(currentPreview);
      if (unresolvedTargets.length === 0) {
        return;
      }

      const { questionIndex, fieldQuestionId, matchedById } =
        resolveQuestionReference(questionId, fallbackQuestionIndex);
      if (!matchedById) {
        return;
      }

      const nextResolvedFields = { ...currentPreview.resolvedFields };
      unresolvedTargets.forEach((target) => {
        nextResolvedFields[suggestionFieldKey(target)] = "rejected";
      });

      const nextPreview = {
        ...currentPreview,
        resolvedFields: nextResolvedFields,
      };

      setPreviewsByQuestionId((prev) => ({
        ...prev,
        [fieldQuestionId]: nextPreview,
        [`index:${questionIndex}`]: nextPreview,
      }));
      completePreviewIfDone(fieldQuestionId, questionIndex, nextPreview);
    },
    [
      completePreviewIfDone,
      handleIgnoreSuggestion,
      resolvePreviewForQuestion,
      resolveQuestionReference,
    ]
  );

  const suggestionConflictReasons = useMemo(() => {
    const nextReasons: Record<string, SuggestionConflictReason> = {};
    // Check if any suggestion across all questions is partially resolved.
    // If so, lock every other suggestion globally to prevent lingering state.
    const hasPartiallyResolvedPreview = visibleQuestionSuggestions.some(
      (bucket) => {
        const preview = resolvePreviewForQuestion(
          bucket.questionId,
          bucket.questionIndex
        );
        return (
          preview != null &&
          preview.affectedFields.length >= 2 &&
          Object.keys(preview.resolvedFields).length > 0 &&
          Object.keys(preview.resolvedFields).length <
            preview.affectedFields.length
        );
      }
    );

    visibleQuestionSuggestions.forEach((bucket) => {
      const acceptedLocks =
        acceptedFieldLocksByQuestionId[bucket.questionId] ?? [];
      const preview = resolvePreviewForQuestion(
        bucket.questionId,
        bucket.questionIndex
      );
      const selectedLockedTargets =
        preview == null
          ? []
          : preview.affectedFields.filter(
              (target) =>
                preview.resolvedFields[suggestionFieldKey(target)] !==
                "rejected"
            );

      bucket.suggestions.forEach((suggestion) => {
        if (selectedSuggestionId === suggestion.id) {
          return;
        }
        const suggestionTargets = suggestionTargetsFromChanges(
          suggestion.fieldChanges
        );
        if (suggestionTargetsConflict(suggestionTargets, acceptedLocks)) {
          nextReasons[suggestion.id] = "accepted-change-conflict";
          return;
        }
        if (hasPartiallyResolvedPreview) {
          nextReasons[suggestion.id] = "partial-application-lock";
          return;
        }
        if (
          preview?.suggestionId !== suggestion.id &&
          suggestionTargetsConflict(suggestionTargets, selectedLockedTargets)
        ) {
          nextReasons[suggestion.id] = "selected-suggestion-conflict";
        }
      });
    });
    return nextReasons;
  }, [
    acceptedFieldLocksByQuestionId,
    resolvePreviewForQuestion,
    selectedSuggestionId,
    visibleQuestionSuggestions,
  ]);

  const handleRefreshSuggestions = useCallback((): void => {
    setAcceptedFieldLocksByQuestionId({});
    setPreviewsByQuestionId({});
    setSelectedSuggestionId(null);
    refreshSuggestions().catch(() => undefined);
  }, [refreshSuggestions]);

  const handleHoverQuestion = useCallback(
    (questionId: string | null, questionIndex: number | null): void => {
      if (questionId && questionIndex != null) {
        const { fieldQuestionId } = resolveQuestionReference(
          questionId,
          questionIndex
        );
        setHighlightedQuestionId(fieldQuestionId);
        return;
      }

      if (activeQuestionId) {
        const activeBucket = visibleQuestionSuggestions.find(
          (bucket) => bucket.questionId === activeQuestionId
        );
        const fallbackIndex = activeBucket?.questionIndex ?? 0;
        const { fieldQuestionId } = resolveQuestionReference(
          activeQuestionId,
          fallbackIndex
        );
        setHighlightedQuestionId(fieldQuestionId);
        return;
      }

      setHighlightedQuestionId(null);
    },
    [activeQuestionId, resolveQuestionReference, visibleQuestionSuggestions]
  );

  return {
    visibleQuestionSuggestions,
    visibleSuggestionCount,
    activeQuestionId,
    highlightedQuestionId,
    selectedSuggestionId,
    ignoringSuggestionId,
    applyingSuggestionId,
    previewsByQuestionId,
    suggestionConflictReasons,
    handleResolveField,
    handleResolveAllFields,
    handleSelectSuggestion,
    handleSelectQuestionTab,
    handleIgnoreSuggestion,
    handleIgnorePreview,
    handleRefreshSuggestions,
    handleHoverQuestion,
  };
};

export default useGuideAssistantPreviewState;
